import React from 'react'
import Layout from '../components/common/Layout'
import { Helmet } from 'react-helmet'

const title = 'I crashed at the same corner as the Lamborghini team. Mine cost nothing.'
const description = 'A simulator crash at the Algarve circuit showed me which corners punish small mistakes before I drove the real track.'
const image = '/images/blog/2026/08/curva-lagos-lamborghini-incident.jpg'
const url = 'https://open-racer.com/what-simulator-crashes-teach-you'
const imageUrl = `https://open-racer.com${image}`
const youtubeId = '3ywWf13w8rE'
const videoUrl = `https://www.youtube.com/watch?v=${youtubeId}`
const embedUrl = `https://www.youtube.com/embed/${youtubeId}`

const Post = () => (
    <>
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="1600" />
            <meta property="og:image:height" content="900" />
            <meta property="og:video" content={embedUrl} />
            <meta property="og:video:secure_url" content={embedUrl} />
            <meta property="og:video:type" content="text/html" />
            <meta property="article:published_time" content="2026-08-11T00:00:00.000+01:00" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />
            <link rel="canonical" href={url} />
            <script type="application/ld+json">{JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                headline: title,
                description,
                datePublished: '2026-08-11T00:00:00.000+01:00',
                image: imageUrl,
                mainEntityOfPage: url,
                video: {
                    '@type': 'VideoObject',
                    name: title,
                    description,
                    thumbnailUrl: imageUrl,
                    embedUrl,
                    uploadDate: '2026-08-11',
                    duration: 'PT28S',
                },
            })}</script>
        </Helmet>
        <Layout>
            <article className="content">
                <header className="post-full-header">
                    <section className="post-full-meta">
                        <time className="post-full-meta-date" dateTime="2026-08-11T00:00:00.000+01:00">
                            11 August, 2026
                        </time>
                        <span className="date-divider">/</span>
                        <a href="/tag/getting-faster">Getting Faster</a>
                    </section>
                    <h1 className="post-full-title">{title}</h1>
                </header>

                <figure
                    className="post-full-image"
                    role="img"
                    aria-label="Onboard view from the following car approaching Curva Lagos at the Algarve circuit"
                    style={{ backgroundImage: `url("${image}")` }}
                />

                <section className="post-full-content">
                    <div className="kg-card-markdown">
                        <p>I bought a professional simulator setup to prepare for the Algarve circuit. Crashing in it turned out to be the most useful part.</p>

                        <p>If you have spent enough time at a race track, you know accidents do not only happen in the corners everyone fears. Cars spin in places that looked harmless from the paddock. A small mistake becomes a very expensive one before the driver has time to fix it.</p>

                        <figure className="kg-card or-article-video">
                            <div className="or-article-video-frame">
                                <iframe
                                    src="https://www.youtube.com/embed/3ywWf13w8rE"
                                    title="Lamborghini incident at Curva Lagos seen from the following car"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="origin"
                                    allowFullScreen
                                />
                            </div>
                            <figcaption>
                                I was driving the following car and watched the Lamborghini ahead lose control at Curva Lagos.
                            </figcaption>
                        </figure>

                        <h2 id="getting-near-the-limit">Getting near the limit</h2>

                        <p>It took me somewhere between 10 and 20 hours of simulator driving, telemetry analysis and onboard videos to get within a tenth of my target lap time. That was the comfortable part. Once I could repeat the lap, I started pushing past it.</p>

                        <p>I changed one thing at a time: a slightly later brake release, a little more entry speed, an earlier throttle application. Most changes made me slower. Some made the car rotate. A few put me into the barrier.</p>

                        <h2 id="losing-the-car-on-purpose">Losing the car on purpose</h2>

                        <p>The circuit model is laser scanned, so the cambers, kerbs, braking references and runoff areas are where they should be. The simulator's vehicle model then shows what those small input changes do to the balance of the car.</p>

                        <p>I stopped treating every crash as a failed lap. I wanted to know what happened after I crossed the limit. Could I catch the slide? Was there enough runoff to slow down? Would the car come back if I stayed calm, or had I already run out of road?</p>

                        <p>Resetting the simulator costs nothing. That made it the right place to ask those questions.</p>

                        <h2 id="the-corners-with-no-way-out">The corners with no way out</h2>

                        <p>Some mistakes were recoverable. Others were decided almost immediately. At certain points, a small change in speed or steering angle put the car on a path to the wall. By the time the slide was obvious, there was no useful correction left.</p>

                        <p>Those became the corners I treated with respect on the real circuit. I still pushed, but I left the margin I had learned I would need. I never spun on the track.</p>

                        <h2 id="the-same-corner-a-very-different-bill">The same corner, a very different bill</h2>

                        <p>The Lamborghini team lost control and put a brand-new car into the wall at the same place where I had crashed in the simulator. I watched several other cars lose it there too.</p>

                        <p>My crash ended with a reset button. Theirs ended with damaged bodywork, recovery vehicles and a stopped session.</p>

                        <p>A simulator cannot reproduce the fear, the g-forces or the consequences of driving a real car. It can show you where those consequences are most likely to start. That was worth more to me than another clean virtual lap.</p>
                    </div>
                </section>
            </article>
        </Layout>
    </>
)

export default Post
